import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Send, Zap, Users, AlertTriangle } from 'lucide-react';

const BLUE = '#0088CC';
const BLUE_LIGHT = '#00CCFF';

const ownerAxios = () => {
  const t = localStorage.getItem('bar_owner_token');
  return axios.create({ baseURL: API, headers: t ? { Authorization: `Bearer ${t}` } : {} });
};

export default function OwnerBroadcastPanel({ barId }) {
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [autoAdsEnabled, setAutoAdsEnabled] = useState(false);
  const [lowThreshold, setLowThreshold] = useState(10);
  const [followers, setFollowers] = useState(0);

  useEffect(() => {
    if (!barId) return;
    const load = async () => {
      try {
        const [set, fol] = await Promise.all([
          axios.get(`${API}/medulla/auto-ads/settings/${barId}`),
          axios.get(`${API}/venue/followers/${barId}`),
        ]);
        setAutoAdsEnabled(set.data.auto_ads_enabled);
        setFollowers(fol.data.followers || 0);
        // low_density_threshold lives in same collection; fetch via a separate endpoint result
        if (typeof set.data.low_density_threshold === 'number') {
          setLowThreshold(set.data.low_density_threshold);
        }
      } catch { /* silent */ }
    };
    load();
  }, [barId]);

  const sendBlast = async () => {
    if (!msg.trim() || !barId) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/notifications/send`, {
        venue_id: barId, message: msg
      });
      toast.success(`📣 Blast sent to ${res.data.sent_count} nearby patrons`);
      setMsg('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSending(false); }
  };

  const sendVibeBroadcast = async () => {
    if (!msg.trim() || !barId) return;
    setSending(true);
    try {
      const res = await ownerAxios().post('/venue/vibe-broadcast', {
        venue_id: barId, message: msg
      });
      toast.success(`🔥 Vibe broadcast fired to ${res.data.sent_count} patrons`);
      setMsg('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSending(false); }
  };

  const toggleAutoAds = async () => {
    try {
      const res = await ownerAxios().post('/medulla/auto-ads/toggle', {
        venue_id: barId, enabled: !autoAdsEnabled
      });
      setAutoAdsEnabled(res.data.enabled);
      toast.success(`Auto-Ads ${res.data.enabled ? 'ON' : 'OFF'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const saveLowThreshold = async () => {
    try {
      await ownerAxios().post('/medulla/low-density-settings', {
        venue_id: barId, low_density_threshold: parseInt(lowThreshold, 10) || 0
      });
      toast.success(`Low-crowd threshold saved: ${lowThreshold}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const manualLowDensityFire = async () => {
    try {
      const res = await axios.post(`${API}/medulla/low-density-trigger`, { venue_id: barId });
      if (res.data.success) {
        toast.success(`Fired — pushed to ${res.data.push_sent_count} patrons`);
      } else {
        toast.info(`Skipped: ${res.data.reason}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const cardStyle = {
    background: 'rgba(10, 20, 35, 0.7)',
    border: `1px solid ${BLUE}66`,
    boxShadow: `0 0 15px ${BLUE}22`,
  };

  return (
    <div className="space-y-4 mt-4">
      {/* Send Push Blast */}
      <Card data-testid="owner-push-blast" className="p-5" style={cardStyle}>
        <h3 className="text-lg font-bold mb-2" style={{ color: BLUE_LIGHT, textShadow: `0 0 10px ${BLUE}` }}>
          🔔 Send Push Blast
        </h3>
        <p className="text-xs text-slate-400 mb-3">
          Fires a Web Push notification to nearby patrons (within 5 miles). {followers > 0 && `${followers} follow your venue.`}
        </p>
        <Textarea
          data-testid="blast-message"
          value={msg}
          onChange={(e) => setMsg(e.target.value.slice(0, 120))}
          placeholder="Line moving fast — come through!"
          className="text-sm mb-2"
          style={{ background: 'rgba(0,0,0,0.6)', border: `1px solid ${BLUE}44`, color: 'white' }}
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">{msg.length} / 120</span>
          <div className="flex gap-2">
            <Button
              data-testid="send-vibe-broadcast"
              onClick={sendVibeBroadcast}
              disabled={sending || !msg.trim()}
              variant="outline"
              size="sm"
              className="border-slate-600"
            >
              <Zap className="w-3 h-3 mr-1" /> Vibe Broadcast
            </Button>
            <Button
              data-testid="send-blast-btn"
              onClick={sendBlast}
              disabled={sending || !msg.trim()}
              size="sm"
              style={{ background: BLUE_LIGHT, color: '#001525' }}
            >
              <Send className="w-3 h-3 mr-1" /> {sending ? 'Sending...' : 'Send Blast'}
            </Button>
          </div>
        </div>
      </Card>

      {/* Auto-Ads Toggle */}
      <Card data-testid="owner-auto-ads-toggle" className="p-5" style={cardStyle}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold" style={{ color: BLUE_LIGHT, textShadow: `0 0 10px ${BLUE}` }}>
              📣 Auto-Ads
            </h3>
            <p className="text-xs text-slate-400 mt-1">
              Auto-generate + post promo content when your vibe score crosses 75.
            </p>
          </div>
          <button
            data-testid="auto-ads-switch"
            onClick={toggleAutoAds}
            className="relative w-14 h-7 rounded-full transition-colors"
            style={{
              background: autoAdsEnabled ? BLUE_LIGHT : '#333',
              boxShadow: autoAdsEnabled ? `0 0 12px ${BLUE_LIGHT}` : 'none'
            }}
          >
            <span
              className="absolute top-0.5 w-6 h-6 rounded-full transition-transform"
              style={{
                background: '#fff',
                left: 2,
                transform: autoAdsEnabled ? 'translateX(28px)' : 'translateX(0)',
              }}
            />
          </button>
        </div>
      </Card>

      {/* Low Density Threshold */}
      <Card data-testid="owner-low-density" className="p-5" style={cardStyle}>
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-4 h-4" style={{ color: BLUE_LIGHT }} />
          <h3 className="text-lg font-bold" style={{ color: BLUE_LIGHT }}>
            Low Crowd Alert Threshold
          </h3>
        </div>
        <p className="text-xs text-slate-400 mb-3">
          Auto-campaign fires when check-ins drop below this number (rolling 2-hour window). Max 1 fire per 4 hours.
        </p>
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-slate-500" />
          <Input
            data-testid="low-threshold-input"
            type="number"
            min="0"
            value={lowThreshold}
            onChange={(e) => setLowThreshold(e.target.value)}
            className="w-24 text-sm"
            style={{ background: 'rgba(0,0,0,0.6)', border: `1px solid ${BLUE}44`, color: 'white' }}
          />
          <Button
            data-testid="save-low-threshold"
            onClick={saveLowThreshold}
            size="sm"
            style={{ background: BLUE, color: '#fff' }}
          >
            Save
          </Button>
          <Button
            data-testid="manual-low-density-fire"
            onClick={manualLowDensityFire}
            size="sm"
            variant="outline"
            className="border-slate-600"
          >
            Fire Now (Manual)
          </Button>
        </div>
      </Card>
    </div>
  );
}
